import * as Accountant from 'app/calcs/Accountant'
import * as ExpenseCalculators from 'app/calcs/expenses'
import { haveEnoughDataForSSACalculation } from 'app/calcs/SSA';
import { MaterialInput } from 'app/components'
import { RetirementAgeSelector } from 'app/components/form/RetirementAgeSelector'
import { HelpfulVideo } from 'app/components/HelpfulVideo'
import MaterialNumber from 'app/components/material/MaterialNumber';
import { IQuestionsGroupContainerProps, QuestionsGroupContainer } from 'app/containers/PipelineSection/QuestionGroupContainer'
import { IRetirementPerson, isValidPerson, isValidPersonForSSA, Profile, updateLifeExpectancy } from 'app/redux/Profile/model'
import { LifeExpectancyService } from 'app/services/LifeExpectancyService';
import * as utils from 'app/utils'
import { getBrandInfo } from 'config'
import { IHelpfulVideo } from 'config/interfaces';
import * as _ from 'lodash'
import * as moment from 'moment'
import * as React from 'react'
import { Button, FormGroup, Modal, Popover } from 'react-bootstrap'
import { Infobox } from '../../../../components/Infobox'

const addPiaToProfile = (
  <Popover id="popover-trigger-click-root-close" >
    Add PIA to <a onClick={() => this.relocate('#/profile')}>Profile</a>
  </Popover>
)

const ageStyle = {
  fontSize: '0.7em',
  height: '5vw'
}

const aStyle = {
  marginBottom: 0,
  lineHeight: 1
}

const piaInfobox = (
  <span>PIA
    <Infobox>
      PIA, or primary insurance amount, is your
      Social Security benefit at your full retirement age.  To input
      this information, you will need a recent statement from the Social
      Security Administration, or you can go
      &nbsp;<a href="https://www.ssa.gov/myaccount/statement.html" target="_blank">here</a>.
      The calculator will use your PIA to estimate your benefits at other ages.
    </Infobox>
  </span>
)

interface ISSAIntroContainerState {
  showFirstVid: boolean
  haveEnoughDataForSSA: boolean
  missingData: any[]
  profile: Profile
  showErrors: boolean
  nowShowingVideo: IHelpfulVideo
}

class SSAIntroContainer extends QuestionsGroupContainer<IQuestionsGroupContainerProps, ISSAIntroContainerState> {
  state: ISSAIntroContainerState = {
    showFirstVid: false,
    haveEnoughDataForSSA: false,
    missingData: [],
    profile: null,
    showErrors: true,
    nowShowingVideo: undefined
  }

  minPIA = 41
  maxPIA = 10000
  minAge = moment().subtract(20, 'years').month(0).date(1).format('YYYY-MM-DD')
  minSocialSecurityAge = 62
  maxSocialSecurityAge = 71
  debouncedSaveUpdatedProfile = _.debounce(() => this.saveUpdatedProfile(), 1000) // chose 1 second to ensure save happens if a page change occurs

  componentWillMount() {
    this.onAppStateUpdate((appState) => {
      // tslint:disable-next-line:no-console
      console.log('ssa intro app state change!')
      const profile = appState.Profile.getNative('info')
      if (profile) {
        const folks = profile.getImportantPeople()
        //const haveEnoughDataForSSA = this.evaluateReadiness(folks)
        const haveEnoughDataForSSA = haveEnoughDataForSSACalculation(folks)
        this.setState({ profile, haveEnoughDataForSSA })
      } else {
        this.setState({ profile })
      }
    })
  }

  evaluateReadiness(folks: IRetirementPerson[]): boolean {
    const missingData = (0 === folks.length) ? ['No member or spouse have been entered.'] : _.reduce(folks, (acc, person) => {
      if (isValidPerson(person)) {

        if (0 === person.PIA) {
          acc.push(<li>The PIA for {person.firstName} is missing.
            <Infobox>You will need a recent statement from the Social Security
            Administration. More information is available at their website
            &nbsp;<a href="https://www.ssa.gov/myaccount/statement.html" target="_blank">here</a>.
            <br/><br/>It is helpful to review your personal financial records before entering the ssa.gov website.
            Look up your Social Security benefit at your full retirement age. This amount is your Primary Insurance Amount.
            </Infobox>
          </li>)
        }

        // tslint:disable-next-line:no-console
        console.log('xxx age: ' + JSON.stringify(person.selectedRetirementAge))
        if (!person.selectedRetirementAge) {
          // tslint:disable-next-line:no-console
          console.log('bang!')
          acc.push(<li>The withdrawal age for {person.firstName} is missing.</li>)
        }
      } else {
        acc.push('not a valid peson: ' + JSON.stringify(person))
      }

      return acc
    }, []);

    this.setState({ missingData })
    return 0 === missingData.length
  }

  memberIsPresentForSSA(): boolean {
    const profile = this.state.profile
    if (!profile) { return false }

    return isValidPersonForSSA(profile.getHeadOfHousehold())
  }

  saveUpdatedProfile() {
    this.Actions.Profile.store(this.state.profile)
  }

  spouseIsPresentForSSA(): boolean {
    const profile = this.state.profile
    if (!profile) { return false }

    return isValidPersonForSSA(profile.getSpouse())
  }

  updatePerson<T extends keyof IRetirementPerson>(memberIndex: number, attr: T, value: IRetirementPerson[T]) {
    const profile = this.state.profile
    const member = this.state.profile.family.members[memberIndex]
    member[attr] = value
    // tslint:disable-next-line:no-console
    console.log('update profile "' + attr + '" for member ' + memberIndex + ' to ' + value)

    // RA-127: changes to these fields cause a life expectancy lookup
    const needLifeExpectancyEvaluation = ['dob', 'selectedRetirementAge']
    if (_.includes(needLifeExpectancyEvaluation, attr)) {
      updateLifeExpectancy(member)
      .then(() => {
        this.setState({ profile })
        this.debouncedSaveUpdatedProfile()
      })
    } else {
      // do this right away
      this.setState({ profile })
      this.debouncedSaveUpdatedProfile()
    }

    if (this.state.haveEnoughDataForSSA) {
      // tslint:disable-next-line:no-console
      console.log('Looks like we need to calc SSA')
      Accountant.runSSAOptimizer(profile.getPeopleForSSA())
      .then(() => undefined)
    }
  }

  updateMember<T extends keyof IRetirementPerson>(attr: T, value: IRetirementPerson[T]) {
    return this.updatePerson(0, attr, value)
  }

  updateSpouse<T extends keyof IRetirementPerson>(attr: T, value: IRetirementPerson[T]) {
    return this.updatePerson(1, attr, value)
  }

  renderHelpfulVideoLinks() {
    if (0 === getBrandInfo().ssaVideos.length) { return null }
    return <article>
      <h4>Helpful Videos</h4>
      <div>
        {_.map(getBrandInfo().ssaVideos, (v, index) => {
          if (v.suppressLine) {
            return (
              <span key={index}>
                &nbsp;<a className="modal-trigger" href="javascript:void(0)"
                  onClick={() => this.setState({ nowShowingVideo: v })}>
                  {v.caption}
                </a>
              </span>)
          } else {
            return (
              <span key={index}>
                {(0 !== index) && <br/>}
                <a className="modal-trigger" href="javascript:void(0)"
                  onClick={() => this.setState({ nowShowingVideo: v })}>
                  <i className="fa fa-fw fa-youtube-play"></i>{v.caption}
                </a>
              </span>)
          }
        })}
      </div>
    </article>
  }

  renderMemberSection() {
    const profile = this.state.profile
    if (!profile) { return null }

    const member = profile.getHeadOfHousehold()
    const memberIsPresent = !_.isUndefined(member)
    const memberPIA = memberIsPresent ? member.PIA : 0
    const memberRetirementAge = moment.duration(member.selectedRetirementAge, 'years')
    const memberNamePossessive = utils.personPossessive(member.firstName)

    return (<div className="memberSection">
      <h6 className="Question"><i className="fa fa-fw fa-user-circle text-success" />
        What is {memberNamePossessive} Social Security Information?</h6>

      <div className="col_2">
      <div>
        <FormGroup>
          <MaterialNumber
            formattedNumberMode
            anyPlaceholder={piaInfobox}
            showValidityErrors={this.state.showErrors}
            value={memberPIA}
            onChange={(e) => {
              const pia = this.toNumber(e.currentTarget.value)
              this.updateMember('PIA', pia)
              if ((pia >= this.minPIA) && (pia <= this.maxPIA)) {
                e.target.setCustomValidity('')
              } else {
                e.target.setCustomValidity('The PIA must be between ' + utils.toCurrency(this.minPIA) + ' and ' + utils.toCurrency(this.maxPIA))
              }
            }}
            required
          />
        </FormGroup>
      </div>

        <FormGroup style={ageStyle}>
          <RetirementAgeSelector min={62} max={70} selectedValue={memberRetirementAge}
          required={true}
          placeholder="Social Security Withdrawal Age"
            onChange={(age) => {
              this.updateMember('selectedRetirementAge', age.asYears())
          }} />
        </FormGroup>

      </div >
    </div>)
  }

  renderSpouseSection() {
    const spouse = this.state.profile.getSpouse()
    if (!isValidPerson(spouse)) { return null }
    const spousePIA = spouse.PIA
    const spouseRetirementAge = moment.duration(spouse.selectedRetirementAge, 'years')
    const spouseNamePossessive = utils.personPossessive(spouse.firstName)

    return (<div className="spouseSection">
      <h6 className="Question"><i className="fa fa-fw fa-user-circle text-success" />
        What is {spouseNamePossessive} Social Security Information?</h6>
      <div className="col_2">
        <FormGroup>
          <MaterialNumber
            anyPlaceholder={piaInfobox}
            formattedNumberMode
            showValidityErrors={this.state.showErrors}
            value={spousePIA}
            onChange={(e) => {
              const pia = this.toNumber(e.currentTarget.value)
              this.updateSpouse('PIA', pia)
              if ((0 === pia) || ((pia >= this.minPIA) && (pia <= this.maxPIA))) {
                e.target.setCustomValidity('')
              } else {
                e.target.setCustomValidity('The PIA must either be 0, or be between ' + utils.toCurrency(this.minPIA) + ' and ' + utils.toCurrency(this.maxPIA))
              }
            }}
            required
          />
        </FormGroup>

        <FormGroup style={ageStyle}>
          <RetirementAgeSelector min={62} max={70} selectedValue={spouseRetirementAge}
            placeholder="Social Security Withdrawal Age"
            required={true}
            onChange={(age) => {
              this.updateSpouse('selectedRetirementAge', age.asYears())
          }} />
        </FormGroup>
      </div>
    </div>)
  }

  renderMissingInfo(person: IRetirementPerson) {
    if (!person) { return null }
    if (!isValidPerson(person)) { return null }

    const missingInfo = []
    if (0 === person.PIA) {
      missingInfo.push(<li>The PIA for {person.firstName} is missing.</li>)
    }

    if (!person.selectedRetirementAge) {
      missingInfo.push(<li>The withdrawl age for {person.firstName} is missing.</li>)
    }

    return missingInfo
  }

  render() {
    return (
      <section className="ssaIntro">
        <h1>Calculate Social Security </h1>
        <h5 className="lead">In this section, you'll provide some information surrounding your Social Security benefits which the calculator can use to estimate your optimal Social Security elections (based on the information available).
        </h5>

        {this.renderHelpfulVideoLinks()}

        <br />
          <Button type="submit" bsStyle="primary" bsSize="lg">
            Get Started
          </Button>

        <Modal show={this.state.showFirstVid}
          onHide={() => this.setState({ showFirstVid: false })}
          className="modal-transparent">
          <Modal.Header closeButton></Modal.Header>
          <Modal.Body style={{ minHeight: 360, maxHeight: 360, padding: 0 }}>
            <iframe src="https://www.youtube.com/embed/a4sXN7XDH-E?ecver=2"
              style={{ position: 'absolute', width: '100%', height: '100%', left: 0 }}
              width="644" height="360"
              frameBorder={0}
              allowFullScreen />
          </Modal.Body>
        </Modal>

        <HelpfulVideo info={this.state.nowShowingVideo} onClose={() => this.setState({ nowShowingVideo: undefined }) } />

      </section >
    )
  }
}

const SSAIntro = () => (
  <SSAIntroContainer
    header="SSA Intro" icon="" />
)

export {
  SSAIntro
}



// WEBPACK FOOTER //
// ./src/app/containers/PipelineSection/SSA/Introduction/index.tsx